import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { safeRPC } from '@/integrations/supabase/utils/rpcCalls';
import { ProductPrice, ProductPriceFormData } from '@/types/productPrice';
import { toast } from '@/hooks/use-toast';
import { products } from '@/data/productData';
import { useMemo } from 'react';

const PRICES_QUERY_KEY = ['product-prices'];
const CACHE_TIME = 5 * 60 * 1000; // 5 minutes
const STALE_TIME = 2 * 60 * 1000; // 2 minutes

export const useProductPrices = () => {
  const queryClient = useQueryClient();

  // Single optimized query with aggressive caching
  const {
    data: prices = [],
    isLoading: loading,
    error,
    refetch
  } = useQuery({
    queryKey: PRICES_QUERY_KEY,
    queryFn: async (): Promise<ProductPrice[]> => {
      const data = await safeRPC<ProductPrice[]>('get_all_product_prices', {}, { timeout: 8000 });
      return data && Array.isArray(data) ? data : [];
    },
    staleTime: STALE_TIME,
    gcTime: CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchInterval: false
  });

  // Memoized price lookup map for performance
  const pricesMap = useMemo(() => {
    return prices.reduce((acc, price) => {
      acc[price.product_id] = price;
      return acc;
    }, {} as Record<number, ProductPrice>);
  }, [prices]);

  // Optimized price getter with fallback
  const getProductPrice = (productId: number): number => {
    const priceData = pricesMap[productId];
    if (!priceData) {
      // Fallback to hardcoded price from productData
      const product = products.find(p => p.id === productId);
      return product?.price || 0;
    }

    // Return sale price if on sale, otherwise regular price
    return priceData.is_on_sale && priceData.sale_price ? priceData.sale_price : priceData.price;
  };

  const getProductPriceData = (productId: number): ProductPrice | null => {
    return pricesMap[productId] || null;
  };

  // Optimized mutation with error handling
  const upsertPriceMutation = useMutation({
    mutationFn: async (priceData: ProductPriceFormData) => {
      const params = {
        p_product_id: priceData.product_id,
        p_price: priceData.price,
        p_original_price: priceData.original_price || null,
        p_sale_price: priceData.sale_price || null,
        p_is_on_sale: priceData.is_on_sale,
        p_currency: priceData.currency,
        p_hidden: priceData.hidden || false
      };
      
      const result = await safeRPC('upsert_product_price', params, { timeout: 8000 });
      if (!result) throw new Error('Failed to update price');
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRICES_QUERY_KEY });
      toast({
        title: "Success",
        description: "Product price updated successfully"
      });
    },
    onError: (error) => {
      console.error('Error updating product price:', error);
      toast({
        title: "Error",
        description: "Failed to update product price",
        variant: "destructive"
      });
    }
  });

  const fetchPrices = () => {
    queryClient.invalidateQueries({ queryKey: PRICES_QUERY_KEY });
  };

  return {
    prices,
    productPrices: pricesMap,
    loading,
    error,
    getProductPrice,
    getProductPriceData,
    fetchPrices,
    refetch: fetchPrices,
    upsertPrice: upsertPriceMutation.mutateAsync,
    isPending: upsertPriceMutation.isPending
  };
};

// Legacy export for backward compatibility
export { useProductPrices as useOptimizedProductPrices };
export { useProductPrices as useProductPricing };